import { observe } from 'mobx';
import { History, Location, Listener } from 'history';
import { RouterStore } from './RouterStore';

/**
 * Strips the app basename from a pathname so the store always works in
 * basename-relative space. history v5 dropped the `basename` option that v4
 * used to provide this; React Router v6 handles the basename at the Router
 * level, so the raw history the store listens to now carries the full path.
 */
const stripBasename = (pathname: string, basename: string): string => {
  const normalized = basename.endsWith('/') ? basename.slice(0, -1) : basename;
  if (!normalized || normalized === '/') {
    return pathname;
  }
  const lowerPathname = pathname.toLowerCase();
  const lowerBasename = normalized.toLowerCase();
  if (lowerPathname === lowerBasename) {
    return '/';
  }
  if (lowerPathname.startsWith(`${lowerBasename}/`)) {
    return pathname.slice(normalized.length);
  }
  return pathname;
};

/**
 * Copied from https://github.com/alisd23/mobx-react-router.
 * Adapted to typescript and mobx6.
 */
export const syncHistoryWithStore = (history: History, store: RouterStore, basename = '') => {
  // Initialise store
  store.history = history;

  // Handle update from history object
  const handleLocationChange = (location: Location) => {
    store._updateLocation(location ? { ...location, pathname: stripBasename(location.pathname, basename) } : location);
  };

  const unsubscribeFromHistory = history.listen(({ location }) => handleLocationChange(location));
  handleLocationChange(history.location);

  // @ts-expect-error
  history.subscribe = (listener: Listener) => {
    const onStoreChange = () => {
      const rawLocation = { ...store.location };
      listener({ location: rawLocation, action: history.action });
    };

    // Listen for changes to location state in store
    const unsubscribeFromStore = observe(store, 'location', onStoreChange);

    listener({ location: store.location, action: history.action });

    return unsubscribeFromStore;
  };
  // @ts-expect-error
  history.unsubscribe = unsubscribeFromHistory;

  return history;
};
